import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../hooks/useAuth";
import useRBAC from "../../../hooks/useRBAC";
import { RootState } from "../../../store";
import apiService from "../../../services/api";
import useHubs from "../../../hooks/useHubs";
import useRoleBasedHubFiltering from "../../../hooks/useRoleBasedHubFiltering";
import useCodeCommonByCategory from "../../../hooks/useCodeCommonByCategory";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";

import {
  FormSection,
  TextInput,
  SelectInput,
  SearchableSelectInput,
  MobileInput,
  FileUploadInput,
} from "../../../components/forms";

import { getInitialFormData } from "./utils/formUtils";
import { useFormValidation, commonValidationRules, ValidationRules } from "../../../components/common/FormValidation";
import { FILE_UPLOAD_CONFIG } from "../../../constants";
import dayjs from 'dayjs';
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
const DEFAULT_IMAGE = "default.png";

// Roles that allow multiple hub assignment (show checkboxes). Others get single hub dropdown.
const MULTI_HUB_ROLES = ["Admin", "Finance", "Hub Cluster Manager", "Support Admin"];

// ====================
// Hooks
// ====================
const useRoles = (setError: (msg: string | null) => void) => {
  const [roles, setRoles] = useState<Array<{ roleid: number; rolename: string }>>([]);
  const [loading, setLoading] = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await apiService.getAllRoles()) as any;
      setRoles(response.data || response || []);
    } catch (error) {
      setError("Failed to load roles. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return { roles, loading };
};



// ====================
// Helper Components
// ====================
const DateField: React.FC<{
  label: string;
  field: string;
  value: string;
  onChange: (field: string, value: string) => void;
  error?: string | null;
  required?: boolean;
}> = ({ label, field, value, onChange, error, required }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DesktopDatePicker
      label={label}
      format="DD/MM/YYYY"
      value={value ? dayjs(value, "DD/MM/YYYY") : null}
      onChange={(newValue: any) => {
        const date = newValue ? (newValue.format ? newValue : dayjs(newValue)) : null;
        onChange(field, date?.format("DD/MM/YYYY") || "")
      }}
       slotProps={{
         textField: {
           size: "small",
           fullWidth: true,
           required: required,
           error: !!error,
           helperText: error,
         },
       }}
    />
  </LocalizationProvider>
);


// ====================
// Main Component
// ====================
const CreateStaffPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { getCreatableRoleNames } = useRBAC();
  const [formData, setFormData] = useState<{ [key: string]: string }>(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assignedHubIds, setAssignedHubIds] = useState<number[]>([]);

  const { roles, loading: rolesLoading } = useRoles(setError);
  const { statusOptions, loading: statusLoading, getCodeValueByDisplayName } = useCodeCommonByCategory('StaffStatus');
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { filterHubOptions } = useRoleBasedHubFiltering();
  
  const createStaffValidationRules: ValidationRules = {
    ...commonValidationRules,
    assignedHub: {
      required: false,
      custom: (value: string, formData: any) => {
        if (formData?.role && MULTI_HUB_ROLES.includes(formData.role)) return undefined;
        if (!value || String(value).trim() === '') return 'Assigned hub is required';
        return undefined;
      }
    },
    password: {
      required: true,
      minLength: 6,
      message: 'Password must be at least 6 characters'
    },
    confirmPassword: {
      required: true,
      custom: (value: string, formData: any) => {
        if (!value) {
          return 'Please confirm the password';
        }
        if (value !== formData.password) {
          return 'Passwords do not match';
        }
        return undefined;
      }
    }
  };
  
  const { isValid: isFormValid, clearFieldError, getFieldError, validateFormData } = useFormValidation(formData, createStaffValidationRules);

  // Filter hub options based on user role
  const filteredHubOptions = filterHubOptions(hubOptions);

  useEffect(() => {
      if (error === 'Please fill all required fields') {
      const requiredFields = [
        "profilePhoto", "fullName", "dateOfBirth", "mobileNo", "email",
        "role",
        "username", "password", "confirmPassword", "status", "emergencyContact"
      ];
      const allFilled = requiredFields.every(f => {
        const fieldValue = formData[f];
        return fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '';
      });
      const hubFilled = formData.role && MULTI_HUB_ROLES.includes(formData.role)
        ? assignedHubIds.length > 0
        : (formData.assignedHub !== undefined && formData.assignedHub !== null && String(formData.assignedHub).trim() !== '');

      if (allFilled && hubFilled) {
        setError(null);
      }
    }
  }, [formData, error, assignedHubIds]);

  // Build preview src from value that can be a URL, path, or JSON array string
  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
    } catch {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
  };

  // ====================
  // Handlers
  // ====================
  const isMultiHubRole = Boolean(formData.role && MULTI_HUB_ROLES.includes(formData.role));

  const handleInputChange = (field: string, value: string) => {
    if (field === "role") {
      if (MULTI_HUB_ROLES.includes(value)) {
        setAssignedHubIds((prev) => prev);
        setFormData((prev) => ({ ...prev, assignedHub: "", [field]: value }));
      } else {
        setAssignedHubIds([]);
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    clearFieldError(field);
  };

  const handleHubCheckboxChange = (hubId: number, checked: boolean) => {
    setAssignedHubIds((prev) =>
      checked ? [...prev, hubId] : prev.filter((id) => id !== hubId)
    );
    clearFieldError("assignedHub");
  };

  const sanitizePayload = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(sanitizePayload);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, v !== "" && v !== undefined ? sanitizePayload(v) : null])
      );
    }
    return obj;
  };

  const mapFormDataToApiPayload = () => {
    // empcode is empty in curl request, API will generate it automatically
    const statusCodeValue = formData.status ? getCodeValueByDisplayName(formData.status) : null;
    const selectedRole = roles.find((r) => r.rolename === formData.role);
    const roleId = selectedRole ? selectedRole.roleid : null;
    const isMulti = Boolean(roleId && formData.role && MULTI_HUB_ROLES.includes(formData.role));

    let hubId: number | null = null;
    let hubname = formData.assignedHub;
    type RoleHubItem = { rolehubrelationid: number; userid: number; roleid: number; hubid: number };
    let roleHubRelationList: RoleHubItem[];

    if (isMulti && assignedHubIds.length > 0) {
      roleHubRelationList = assignedHubIds.map((hubid) => ({
        rolehubrelationid: 0,
        userid: 0,
        roleid: roleId ?? 0,
        hubid,
      }));
      hubId = roleHubRelationList[0]?.hubid ?? null;
      const names = assignedHubIds
        .map((id) => hubOptions.find((h) => h.hubmasterid === id)?.hubname)
        .filter(Boolean);
      hubname = names.join(", ");
    } else {
      const selectedHub = hubOptions.find((h) => h.hubname === formData.assignedHub);
      hubId = selectedHub ? selectedHub.hubmasterid : null;
      roleHubRelationList = [
        { rolehubrelationid: 0, userid: 0, roleid: roleId ?? 0, hubid: hubId ?? 0 },
      ];
    }
    const roleHubRelationModel = roleHubRelationList[0] ?? { rolehubrelationid: 0, userid: 0, roleid: roleId ?? 0, hubid: hubId ?? 0 };

    // Convert dates from DD/MM/YYYY to ISO format (YYYY-MM-DDTHH:mm:ss)
    const convertDateToISO = (dateStr: string): string => {
      if (!dateStr) return "";
      const parsed = dayjs(dateStr, "DD/MM/YYYY");
      if (!parsed.isValid()) return "";
      return parsed.format("YYYY-MM-DDTHH:mm:ss");
    };

    const dobISO = convertDateToISO(formData.dateOfBirth);
    const joiningDateISO = convertDateToISO(formData.joiningDate);
    
    // Format userjoiningdate as YYYY-MM-DDTHH:mm:ss (from curl: 2021-11-10T00:00:00)
    // Using current date/time for now, but can be set to a specific date if needed
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const userJoiningDate = `${year}-${month}-${day}T00:00:00`;

    const payload = {
      userid: 0,
      name: formData.fullName,
      emailid: formData.email,
      password: formData.password,
      profilephoto: formData.profilePhoto,
      phonenumber: formData.mobileNo,
      terms_condition_accepted: false,
      userstatus: 1,
      bannedreason: "",
      createdby: user?.userid || 0,
      modifiedby: user?.userid || 0,
      roleid: roleId,
      rolename: formData.role,
      dob: dobISO,
      hubid: hubId,
      empcode: "",
      username: formData.username,
      hubname,
      staffstatus: statusCodeValue || 0,
      userjoiningdate: userJoiningDate,
      roleHubRelationList,
      roleHubRelationModel,
      employmentdetail: {
        empdetailid: 0,
        userid: 0,
        empdesignation: formData.designation,
        joiningdate: joiningDateISO,
        status: true,
        emergencycontact: formData.emergencyContact,
        specimen_e_signature: formData.specimenESignature,
      },
    };

    return sanitizePayload(payload);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!validateFormData(formData)) {
      setError("Please fill all required fields");
      return;
    }
    if (isMultiHubRole && assignedHubIds.length === 0) {
      setError("Please select at least one hub.");
      return;
    }

    setLoading(true);
    try {
      const payload = mapFormDataToApiPayload();
      const response = (await apiService.createOrUpdateUser(payload)) as any;

      // Check if response has isSuccess field (wrapped response)
      if (response?.isSuccess === false) {
        setError(response.message || "Failed to create staff member.");
        setLoading(false);
        return;
      }

      // Check if response is the user object directly (has userid) or wrapped in data
      const userData = response?.data || response;
      
      if (userData?.userid) {
        // Success: response contains userid
        setSuccess(`User created successfully! (ID: ${userData.userid})`);
        setTimeout(() => {
          navigate('/staff-management', { state: { refresh: Date.now() } });
        }, 1200);
      } else if (response?.isSuccess === true && response?.data) {
        // Success: wrapped response with isSuccess
        setSuccess(`User created successfully! (ID: ${response.data.userid})`);
        setTimeout(() => {
          navigate('/staff-management', { state: { refresh: Date.now() } });
        }, 1200);
      } else {
        // Failure: no userid found
        setError(response?.message || "Failed to create staff member.");
      }
    } catch (err: any) {
      console.error("Error creating staff:", err);
      setError(err.message || err.response?.data?.message || "Failed to create staff member.");
    } finally {
      setLoading(false);
    }
  };

  // ====================
  // Sections
  // ====================
  const renderIdentitySection = () => (
    <FormSection title="Identity & Contact">
      <Row className="g-3">
        <Col xs={12} md={6} lg={3}>
          <FileUploadInput
            field="profilePhoto"
            formData={formData}
            onChange={handleInputChange}
            folderPath={FILE_UPLOAD_CONFIG.FOLDER_PATHS.STAFF}
            folderType={FILE_UPLOAD_CONFIG.FOLDER_TYPES.PROFILE}
            required
            error={getFieldError("profilePhoto")}
          />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <TextInput
            field="fullName"
            formData={formData}
            onChange={handleInputChange}
            label="Full Name"
            required
            placeholder="Enter full name"
            error={getFieldError("fullName")}
          />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <DateField
            label="Date of Birth"
            field="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            error={getFieldError("dateOfBirth")}
            required
          />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <MobileInput
            field="mobileNo"
            formData={formData}
            onChange={handleInputChange}
            placeholder="Enter mobile number"
            required
            error={getFieldError("mobileNo")}
          />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <TextInput
            field="email"
            formData={formData}
            onChange={handleInputChange}
            label="Email"
            type="email"
            required
            placeholder="Enter email"
            error={getFieldError("email")}
          />
        </Col>
      </Row>
    </FormSection>
  );

  const renderRoleSection = () => (
    <FormSection title="Role & Assignment">
      <Row className="g-3">
        <Col xs={12} md={6} lg={3}>
          <SelectInput
            field="role"
            formData={formData}
            onChange={handleInputChange}
            label="Role"
            required
            disabled={rolesLoading}
            options={roles
                    .filter(
                      (r) =>
                        r.rolename !== "SuperAdmin" &&
                        r.rolename !== "User" &&
                        getCreatableRoleNames().includes(r.rolename)
                    )
                    .map((r) => r.rolename)}
            error={getFieldError("role")}
          />
        </Col>
        <Col xs={12} md={6} lg={isMultiHubRole ? 12 : 3}>
          {isMultiHubRole ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Assigned Hubs <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <FormGroup row sx={{ flexWrap: "wrap", gap: 0.5 }}>
                {filteredHubOptions.map((hub: any) => (
                  <FormControlLabel
                    key={hub.hubmasterid}
                    control={
                      <Checkbox
                        checked={assignedHubIds.includes(hub.hubmasterid)}
                        onChange={(e) => handleHubCheckboxChange(hub.hubmasterid, e.target.checked)}
                        disabled={hubsLoading}
                        size="small"
                      />
                    }
                    label={hub.hubname}
                  />
                ))}
              </FormGroup>
              {getFieldError("assignedHub") && (
                <FormHelperText error>{getFieldError("assignedHub")}</FormHelperText>
              )}
            </Box>
          ) : (
            <SearchableSelectInput
              field="assignedHub"
              formData={formData}
              onChange={handleInputChange}
              label="Assigned Hub"
              required
              disabled={hubsLoading}
              placeholder="Search and select hub"
              options={
                hubsLoading
                  ? []
                  : filteredHubOptions.map((hub: any) => hub.hubname)
              }
              error={getFieldError("assignedHub")}
            />
          )}
        </Col>
      </Row>
    </FormSection>
  );

  const renderEmploymentSection = () => (
    <FormSection title="Employment & Operations">
      <Row className="g-3">
        <Col xs={12} md={6} lg={3}>
          <TextInput field="designation" formData={formData} onChange={handleInputChange} label="Designation" placeholder="Enter designation" />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <DateField label="Joining Date" field="joiningDate" value={formData.joiningDate} onChange={handleInputChange} />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <SelectInput 
            field="status" 
            formData={formData} 
            onChange={handleInputChange} 
            label="Status" 
            required
            disabled={statusLoading}
            options={statusOptions}
            error={getFieldError("status")}
          />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <MobileInput 
            field="emergencyContact" 
            formData={formData} 
            onChange={handleInputChange} 
            placeholder="Emergency Contact"
            required
            error={getFieldError("emergencyContact")}
          />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <TextInput field="specimenESignature" formData={formData} onChange={handleInputChange} label="Specimen E-Signature" placeholder="Enter specimen signature" />
        </Col>
      </Row>
    </FormSection>
  );

  const renderSystemAccessSection = () => (
    <FormSection title="System Access">
      <Row className="g-3">
        <Col xs={12} md={6} lg={3}>
          <TextInput field="username" formData={formData} onChange={handleInputChange} label="User Name" required placeholder="Enter user name" error={getFieldError("username")} />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <TextInput field="password" formData={formData} onChange={handleInputChange} label="Password" type="password" required placeholder="Enter password" error={getFieldError("password")} />
        </Col>
        <Col xs={12} md={6} lg={3}>
          <TextInput field="confirmPassword" formData={formData} onChange={handleInputChange} label="Confirm Password" type="password" required placeholder="Confirm password" error={getFieldError("confirmPassword")} />
        </Col>
      </Row>
    </FormSection>
  );

  const renderAttachmentsSection = () => (
    <FormSection title="Attachments">
      <Row className="g-3">
        {formData.profilePhoto && (
          <Col xs={12} sm={6} md={4}>
            <ImagePreviewCard
              title="Profile Image"
              value={formData.profilePhoto}
              resolveSrc={resolvePreviewSrc}
              size={200}
              defaultImage={DefaultImage}
              onDelete={() => setFormData((prev) => ({ ...prev, profilePhoto: "" }))}
            />
          </Col>
        )}

        {!formData.profilePhoto && (
          <Col xs={12}>
            <Box sx={{ p: 4, textAlign: "center", backgroundColor: "#f8f9fa", borderRadius: 2, border: "2px dashed #dee2e6" }}>
              <Typography variant="body1" sx={{ color: "#6c757d" }}>No attachments uploaded yet</Typography>
            </Box>
          </Col>
        )}
      </Row>
    </FormSection>
  );

  // ====================
  // Render
  // ====================
  return (
    <Container fluid>
      {error && <Alert severity="error" className="mb-3" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" className="mb-3" onClose={() => setSuccess(null)}>{success}</Alert>}

      {renderIdentitySection()}
      {renderRoleSection()}
      {renderEmploymentSection()}
      {renderSystemAccessSection()}
      {renderAttachmentsSection()}

      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          disabled={loading || !isFormValid || (isMultiHubRole && assignedHubIds.length === 0)}
          onClick={handleSave}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "130px",
            "&:disabled": { backgroundColor: "rgba(13,44,90,0.5)" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" /> Saving...
            </Box>
          ) : "Save"}
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: "rgba(13,44,90,1)",
            color: "rgba(13,44,90,1)",
            "&:hover": { borderColor: "rgba(13,44,90,1)", backgroundColor: "rgba(13,44,90,0.04)" },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default CreateStaffPage;
